import React, {useState, useEffect, useImperativeHandle, forwardRef, ChangeEvent} from 'react'
import {Alert} from '../UI/index.js'
import {FormRow, Input} from '../UI/forms/index.js'
import {Customer, CustomersObject} from '../../../types/types.js'
import {normalize} from '@utils/normalize.js'

interface Contact {
    name: string
    phone: string
    email: string
    role?: string
    notes?: string
}

interface SelectCustomerProps {
    customers: CustomersObject
    value: string
    contact?: string | Contact
    showNotes?: boolean
    onChange: (data: {customer?: Customer, contact?: Contact}) => void
    disabled?: boolean
}

export interface SelectCustomerRef {
    validate: () => boolean
    focus: () => void
}

const SelectCustomer = forwardRef<SelectCustomerRef, SelectCustomerProps>((props, ref) => {
    const {customers, value, contact, showNotes, onChange, disabled} = props

    const [customer, setCustomer] = useState<Customer | undefined>(customers[value])
    const [customerName, setCustomerName] = useState<string>(customers[value]?.name || '')
    const [validation, setValidation] = useState<{customer?: boolean}>({})

    useEffect(() => {
        if (value !== undefined) {
            const newCustomer = customers[value]
            setCustomer(newCustomer)
            setCustomerName(newCustomer ? newCustomer.name : '')
        }
    }, [value, customers])

    const handleSetCustomer = (event: ChangeEvent<HTMLInputElement>) => {
        const newValidation = {...validation, customer: false}

        const inputValue = event.target.value
        const newCustomerName = inputValue.length === 1 ? inputValue.toUpperCase() : inputValue
        const foundCustomer = Object.values(customers).find((c) => c.name === newCustomerName)

        setCustomerName(newCustomerName)
        setValidation(newValidation)

        if (foundCustomer) {
            onChange({customer: foundCustomer})
        }
    }

    const handleSetContact = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value
        const name = inputValue.length === 1 ? inputValue.toUpperCase() : inputValue

        let newContact: Contact = {name, phone: '', email: ''}

        if (customer && customer.contacts) {
            const foundContact = customer.contacts.find((c) => c.name === name)
            if (foundContact) {
                newContact = foundContact as Contact
            }
        }

        onChange({contact: newContact})
    }

    const validate = (): boolean => {
        const newValidation: {customer?: boolean} = {}
        let id = ''
        let valid = true

        if (!customer) {
            id = 'customerselect'
            newValidation.customer = true
            valid = false
        }

        if (!valid) {
            const element = document.getElementById(id)
            if (element) {
                element.focus()
            }
        }

        setValidation(newValidation)
        return valid
    }

    const focus = () => {
        const element = document.getElementById('customerselect')
        if (element) {
            element.focus()
        }
    }

    useImperativeHandle(ref, () => ({
        validate,
        focus
    }))

    const suggestions = customerName.length < 1 ?
        [] :
        Object.values(customers).filter((cust) => {
            return normalize(cust.name.toLowerCase()).indexOf(normalize(customerName.toLowerCase())) > -1 && !cust.isArchived
        }).slice(0, 9)

    return (
        <div style={{width: '100%'}}>
            <FormRow>
                <Input
                    id='customerselect'
                    label='Klant'
                    suggestions={suggestions.map((s) => s.name)}
                    value={customerName}
                    isInvalid={validation.customer}
                    onChange={handleSetCustomer}
                    disabled={disabled}
                />

                {typeof contact === 'string' &&
                    <Input
                        id='contact'
                        label='Contactpersoon'
                        suggestions={customer ? customer.contacts.map((c) => c.name) : []}
                        value={contact}
                        onChange={handleSetContact}
                        disabled={disabled}
                    />
                }
            </FormRow>

            {showNotes && customer?.settings.orders.newOrderInstructions &&
                <Alert variant='info' style={{marginBottom: 6, marginTop: 2}}>{customer.settings.orders.newOrderInstructions}</Alert>
            }
        </div>
    )
})

SelectCustomer.displayName = 'SelectCustomer'

export default SelectCustomer
